<template>
  <!-- ?debug=true のときだけ右下に floating button、 クリックで設定パネル展開 -->
  <div v-if="enabled" class="fixed bottom-4 right-4 z-50 q4cl-fixed-fontsize">
    <button
      v-if="!open"
      type="button"
      class="debug-fab"
      @click="open = true"
      :aria-label="$t('デバッグ設定を開く')"
      title="DEBUG"
    >
      <i class="fa-solid fa-bug" aria-hidden="true"></i>
      <span class="ml-1 font-bold tracking-wide">DEBUG</span>
    </button>
    <div
      v-else
      class="debug-panel"
      role="dialog"
      aria-labelledby="debug-panel-title"
    >
      <header class="flex items-center justify-between mb-3">
        <h3 id="debug-panel-title" class="font-bold text-q4cl-primary text-sm flex items-center gap-1.5">
          <i class="fa-solid fa-bug text-amber-600" aria-hidden="true"></i>
          {{ $t('デバッグ設定') }}
        </h3>
        <button type="button" class="btn-icon !min-h-0 !min-w-0 !p-1 text-xs" @click="open = false" :aria-label="$t('閉じる')">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </header>

      <div class="space-y-2 mb-3">
        <label
          v-for="def in defs"
          :key="def.key"
          class="flex items-start gap-2 text-xs cursor-pointer hover:bg-q4cl-surface px-1.5 py-1 rounded"
          :class="urlOverridden(def.key) ? 'opacity-60' : ''"
        >
          <input
            type="checkbox"
            class="mt-0.5 accent-q4cl-accent"
            :checked="opts[def.key]"
            :disabled="urlOverridden(def.key)"
            @change="opts.set(def.key, $event.target.checked)"
          />
          <span class="flex-1">
            <span class="font-medium text-q4cl-ink">{{ def.label }}</span>
            <span v-if="urlOverridden(def.key)" class="ml-1 text-[10px] text-amber-700 font-bold">URL固定</span>
            <br />
            <code class="text-[10px] text-q4cl-muted font-mono">?{{ def.param }}={{ opts[def.key] }}</code>
          </span>
        </label>
      </div>

      <div class="border-t border-q4cl-border pt-3 space-y-2">
        <button
          type="button"
          class="w-full btn-secondary !py-1.5 !px-2 text-xs"
          @click="autoFill"
          :disabled="filling"
        >
          <i class="fa-solid fa-wand-magic-sparkles mr-1" aria-hidden="true"></i>
          {{ filling ? $t('入力中...') : $t('全フィールドにダミー値を入力') }}
        </button>
        <button
          type="button"
          class="w-full btn-secondary !py-1.5 !px-2 text-xs"
          @click="reload"
        >
          <i class="fa-solid fa-rotate mr-1" aria-hidden="true"></i>
          {{ $t('リロード') }}
        </button>
      </div>

      <p class="mt-3 pt-2 border-t border-q4cl-border text-[10px] text-q4cl-muted leading-relaxed">
        URL 引数の例:<br />
        <code class="font-mono">?debug=true&amp;debug_eval_log=false</code><br />
        URL で設定された項目は UI から変更不可。
      </p>
    </div>
  </div>
</template>

<script>
/**
 * デバッグパネル — ?debug=true のときだけ表示
 *  - 各ログ系のトグル (eval / payload / firebase write)
 *  - 起動ダイアログの自動 skip (welcome / answerId notice)
 *  - 全フィールドへのダミー値入力 (autoFill) — 検証用
 *  - 設定変更は localStorage に永続化 + URL 引数が常に優先
 */
export default {
  name: 'DebugPanel',
  setup() {
    const opts = window.debugOpts;
    const enabled = Vue.computed(() => opts?.enabled);
    const open = Vue.ref(false);
    const filling = Vue.ref(false);

    const LABELS = {
      evalLog: 'eval 計算ログ ([eval] key formula => value)',
      payloadLog: '送信 payload ログ ([Q4CL] 送信 / [Q4CL:payload])',
      firebaseWrite: 'debug でも Firebase 実書き込み',
      skipWelcome: 'ウェルカムダイアログ自動 skip',
      skipAnsIdNotice: '回答番号事前周知ダイアログ自動 skip'
    };

    const defs = Vue.computed(() => (opts?._defs || []).map((d) => ({
      ...d,
      label: LABELS[d.key] || d.key
    })));

    function urlOverridden(key) {
      return opts?.urlOverridden(key) || false;
    }

    function reload() {
      window.location.reload();
    }

    // 各フィールド型に応じたダミー値を当てる (Vue reactive 経由で書き換え)
    function dummyFor(field) {
      switch (field.type) {
        case 'text': {
          if (field.name === 'ptName') return '山田 太郎';
          if (field.name === 'ptId') return '12345';
          if (field.name === 'ptPhone') return '090-1234-5678';
          if (field.name === 'ptEmail') return 'test@example.com';
          if (field.name === 'ptNameKana') return 'ヤマダ タロウ';
          if (field.name === 'ptAddress') return '東京都千代田区千代田 1-1';
          if (field.inputmode === 'numeric' || field.inputmode === 'decimal') return '42';
          return 'テスト入力';
        }
        case 'textarea':
          return 'テスト入力テキストです。\nここはダミー値です。';
        case 'date':
          return '1990-01-01';
        case 'time':
          return '09:30';
        case 'scale': {
          if (field.subtype === 'painscale') return 3;
          if (field.subtype === 'bodytemp') return 365;
          if (field.subtype === 'bloodpressure') return { upper: 120, lower: 80 };
          const min = field.min ?? 0;
          const max = field.max ?? 100;
          return Math.round((min + max) / 2);
        }
        default:
          return undefined;
      }
    }

    async function autoFill() {
      filling.value = true;
      try {
        // 起動済みの QuestionnaireForm の reactive fields にアクセスする。
        // CustomEvent で fields 参照をもらう (双方向に store を作るより疎結合)
        const ev = new CustomEvent('q4cl:debug-request-fields', { detail: { resolve: null } });
        let fields = null;
        const handler = (e) => {
          fields = e.detail.fields || null;
        };
        window.addEventListener('q4cl:debug-provide-fields', handler);
        window.dispatchEvent(new CustomEvent('q4cl:debug-request-fields'));
        // 1 tick 待つ
        await new Promise((r) => setTimeout(r, 50));
        window.removeEventListener('q4cl:debug-provide-fields', handler);

        if (!fields) {
          alert('QuestionnaireForm が見つかりません (起動前の可能性)');
          return;
        }

        let count = 0;
        for (const id in fields) {
          const f = fields[id];
          // readonly はスキップ
          if (f.readonly === true) continue;

          switch (f.type) {
            case 'text': {
              const v = dummyFor(f);
              if (v !== undefined && !f.value) { f.value = v; count++; }
              break;
            }
            case 'textarea': {
              if (!f.text) { f.text = dummyFor(f); count++; }
              break;
            }
            case 'date':
            case 'time': {
              if (!f.value) { f.value = dummyFor(f); count++; }
              break;
            }
            case 'scale': {
              const v = dummyFor(f);
              if (v !== undefined) {
                if (f.subtype === 'bloodpressure') {
                  if (!f.value || (f.value.upper === 0 && f.value.lower === 0)) {
                    f.value = v;
                    count++;
                  }
                } else if (f.value == null || f.value < 0 || f.value === 0) {
                  // painscale は -1 が未選択、 bodytemp は 0 が未測定の sentinel
                  f.value = v;
                  count++;
                }
              }
              break;
            }
            case 'radio': {
              if (!f.value && f.sels) {
                const keys = Object.keys(f.sels);
                if (keys.length > 0) {
                  const firstKey = keys[0];
                  for (const k in f.sels) f.sels[k].checked = (k === firstKey);
                  f.value = f.sels[firstKey].value;
                  count++;
                }
              }
              break;
            }
            case 'checkbox': {
              if (f.sels) {
                const anyChecked = Object.values(f.sels).some((s) => s.checked);
                if (!anyChecked) {
                  const keys = Object.keys(f.sels);
                  if (keys.length > 0) {
                    f.sels[keys[0]].checked = true;
                    count++;
                  }
                }
              }
              break;
            }
            case 'formattedInput': {
              // FormattedInput は内部で field.value を構築するので明示は難しい。 スキップ。
              break;
            }
            default:
              break;
          }
        }
        console.log('[debug] autoFill: 入力済みフィールド数 =', count);
        // 入力した内容を「変更あり」 にマークするため、 ヘッダーに通知 (changed.value 経由)
        window.dispatchEvent(new CustomEvent('q4cl:debug-marked-changed'));
      } finally {
        filling.value = false;
      }
    }

    return { enabled, open, opts, defs, urlOverridden, reload, autoFill, filling };
  }
};
</script>

<style>
.debug-fab {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.85rem;
  background: #B45309;          /* amber-700 — 警告色寄り */
  color: #FFFFFF;
  font-size: 0.75rem;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(180, 83, 9, 0.35);
  cursor: pointer;
  transition: background-color 0.12s ease;
  font-family: monospace;
}
.debug-fab:hover { background: #92400E; }       /* amber-800 */
.debug-fab:focus-visible { outline: 2px solid #FFFFFF; outline-offset: 2px; }

.debug-panel {
  background: #FFFFFF;
  border: 2px solid #B45309;
  border-radius: 10px;
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.25), 0 8px 10px -6px rgba(15, 23, 42, 0.1);
  padding: 0.85rem 1rem;
  width: 320px;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
}
</style>
